import { produce } from 'immer';
import { appContext } from '../AppContext';
import { UserPersistedStateShowConfirmationModal } from '../persistedstate/users/userpersistedstate-types';

const MSG_SHOW = 'MSG_SHOW';
const MSG_CLOSE = 'MSG_CLOSE';
const MSG_UPDATE = 'MSG_UPDATE';
const MSG_WORKING = 'MSG_WORKING';

export type MessagesState = MessageState[];
export type MessageState = {
  id: number;
  message: string;
  working: boolean;
  text: string;
  subText: string | React.ReactNode;
  type: 'banner' | 'alert' | 'confirm' | 'prompt';
  multiLine: boolean;
  maxLength: number;
  title: string;
  noText: string;
  yesText: string;
  cancelText: string;
  okText: string;
  linkText: string;
  linkUrl: string;
  level: number;
  messageTooltip?: string;
  skipConfirmationModalKey: keyof UserPersistedStateShowConfirmationModal;
  className: string;
  /**
   * Optional function to validate user input in prompt dialogs.
   * Returns an object with an optional error message if validation fails.
   * @property {string} [onValidateInput.returns.errorMessage] - Error message if validation fails.
   */
  onValidateInput?: (value: string) => { errorMessage?: string };
  placeholder?: string;
};

export type MessageOption = { msg?: string | React.ReactNode } & Partial<MessageState>;
export type MessageCallback = (result: boolean, text: string, close: boolean, action: MessageAction) => void | Promise<void>;

export const callbackFunctions: { [name: string | number]: MessageCallback } = {};

export enum MessageAction {
  CLOSE = 'close',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary'
}

export const closeMessage = async (id: string | number, didConfirm?: boolean, text?: string, didClose?: boolean, action?: MessageAction) => {
  if (callbackFunctions[id]) {
    appContext.store.dispatch({
      type: MSG_WORKING,
      payload: {
        id
      }
    });

    await Promise.resolve(callbackFunctions[id](didConfirm, text, didClose, action));
  }
  delete callbackFunctions[id];
  appContext.store.dispatch({
    type: MSG_CLOSE,
    payload: {
      id
    }
  });
};

export const showBanner = (message: string, level?: TypeMePlease, linkText?: string, linkUrl?: string, duration?: number) => {
  const id = Math.random();
  appContext.store.dispatch({
    type: MSG_SHOW,
    payload: {
      type: 'banner',
      message,
      id,
      level,
      linkText,
      linkUrl
    }
  });
  setTimeout(() => {
    closeMessage(id, false);
  }, duration ?? 5000);
};

export const showConfirm = (options: MessageOption, callback: MessageCallback) => {
  const id = Math.random();
  const { msg, title } = options;
  callbackFunctions[id] = callback;
  appContext.store.dispatch({
    type: MSG_SHOW,
    payload: {
      type: 'confirm',
      title,
      message: msg,
      id,
      yesText: options.yesText || 'Yes',
      noText: options.noText || (!options.cancelText && 'No'),
      cancelText: options.cancelText,
      messageTooltip: options.messageTooltip,
      skipConfirmationModalKey: options.skipConfirmationModalKey,
      className: options.className || ''
    }
  });
};
export const confirm = showConfirm;

export const showPrompt = (options: MessageOption, callback: MessageCallback) => {
  const id = Math.random();
  const { msg, title, text } = options;
  const multiLine = options.multiLine || false;
  const maxLength = options.maxLength || 100;
  callbackFunctions[id] = callback;
  appContext.store.dispatch({
    type: MSG_SHOW,
    payload: {
      type: 'prompt',
      title,
      message: msg,
      id,
      okText: options.okText || 'Ok',
      cancelText: 'Cancel',
      text: (text || '').substring(0, maxLength),
      maxLength,
      multiLine,
      subText: options.subText || null,
      onValidateInput: options.onValidateInput,
      placeholder: options.placeholder || ''
    }
  });
};

export const showMessage = (title: string, message?: string, callback?: MessageCallback, okText?: string) => {
  const id = Math.random();
  if (callback) {
    callbackFunctions[id] = callback;
  }
  appContext.store.dispatch({
    type: MSG_SHOW,
    payload: {
      type: 'alert',
      title,
      message,
      id,
      okText: okText || 'Ok'
    }
  });
  return id;
};

export const alert = showMessage;

export const updateMessage = (id: string, message: string) => {
  appContext.store.dispatch({
    type: MSG_UPDATE,
    payload: {
      message,
      id
    }
  });
};

const closeMessageReducer = (state: MessagesState, id: number) => {
  const messageIndex = state.findIndex((x) => x.id === id);
  if (messageIndex > -1) {
    state.splice(messageIndex, 1);
  }
  return state;
};

const updateMessageReducer = (state: MessagesState, id: number, message: string) => {
  const messageIndex = state.findIndex((x) => x.id === id);
  if (messageIndex > -1) {
    state[messageIndex].message = message;
  }
  return state;
};

const updateWorkingReducer = (state: MessagesState, id: number, working: boolean) => {
  const messageIndex = state.findIndex((x) => x.id === id);
  if (messageIndex > -1) {
    state[messageIndex].working = working;
  }
  return state;
};

export const messageBoxReducer = (state: MessagesState = [], action): MessagesState => {
  if (!action) return state;
  return produce(state, (draft) => reduce(draft, action));
};

function reduce(state: MessagesState, action): MessagesState {
  switch (action.type) {
    case MSG_SHOW:
      state.push(action.payload);
      return state;
    case MSG_CLOSE:
      return closeMessageReducer(state, action.payload.id);
    case MSG_UPDATE:
      return updateMessageReducer(state, action.payload.id, action.payload.message);
    case MSG_WORKING:
      return updateWorkingReducer(state, action.payload.id, true);

    default:
      return state;
  }
}
